var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/context/MemberstackProvider.tsx
import React from "react";
import msDom from "@memberstack/dom";
var MemberstackContext = React.createContext({ memberstack: null });
var useMemberstackContext = () => {
  return React.useContext(MemberstackContext);
};
var MemberstackProvider = (props) => {
  const config = __spreadValues({
    useCookies: true
  }, props.config);
  const memberstack = msDom.init(config);
  return /* @__PURE__ */ React.createElement(MemberstackContext.Provider, { value: { memberstack } }, props.children);
};

// src/context/MemberstackProtected.tsx
import React2 from "react";

// src/hooks/useMemberstack.tsx
var useMemberstack = () => {
  const { memberstack } = useMemberstackContext();
  if (!memberstack) {
    throw new Error("useMemberstack can only be used within <MemberstackProvider>");
  }
  return memberstack;
};

// src/context/MemberstackProtected.tsx
var MemberstackProtected = (props) => {
  const memberstack = useMemberstack();
  const [state, setState] = React2.useState("LOADING" /* LOADING */);
  if (!memberstack) {
    throw new Error("<MemberstackProtected> can only be used within <MemberstackProvider>");
  }
  if (props.onUnauthorized && typeof props.onUnauthorized !== "function" && !React2.isValidElement(props.onUnauthorized)) {
    throw new Error("onUnauthorized must be a function or a valid React element");
  }
  function getMemberAuthorization(member) {
    if (!member) {
      setState("UNAUTHORIZED" /* UNAUTHORIZED */);
      return;
    }
    const { permissions, plans } = props.allow || {};
    if (permissions == null ? void 0 : permissions.length) {
      if (!permissions.some((per) => {
        var _a;
        return (_a = member == null ? void 0 : member.permissions) == null ? void 0 : _a.includes(per);
      })) {
        setState("UNAUTHORIZED" /* UNAUTHORIZED */);
        return;
      }
    }
    if (plans == null ? void 0 : plans.length) {
      if (!plans.some((planId) => {
        var _a;
        return (_a = member == null ? void 0 : member.planConnections) == null ? void 0 : _a.find((con) => con.planId === planId);
      })) {
        setState("UNAUTHORIZED" /* UNAUTHORIZED */);
        return;
      }
    }
    setState(member ? "AUTHORIZED" /* AUTHORIZED */ : "UNAUTHORIZED" /* UNAUTHORIZED */);
  }
  function getMember() {
    return __async(this, null, function* () {
      const { data: member } = yield memberstack.getCurrentMember();
      getMemberAuthorization(member);
    });
  }
  React2.useEffect(() => {
    let isMounted = true;
    let listener;
    if (isMounted) {
      listener = memberstack == null ? void 0 : memberstack.onAuthChange(getMemberAuthorization);
      getMember();
    }
    return () => {
      isMounted = false;
      listener.unsubscribe();
    };
  }, []);
  switch (state) {
    case "LOADING" /* LOADING */:
      return props.fallback || null;
    case "AUTHORIZED" /* AUTHORIZED */:
      return props.children;
    case "UNAUTHORIZED" /* UNAUTHORIZED */:
      if (React2.isValidElement(props.onUnauthorized)) {
        return props.onUnauthorized;
      } else if (typeof props.onUnauthorized === "function") {
        props.onUnauthorized();
      }
      return null;
    default:
      return null;
  }
};

// src/hooks/useAuth.tsx
import React3 from "react";
var useAuth = () => {
  const memberstack = useMemberstack();
  const [auth, setAuth] = React3.useState(null);
  function getMemberAuthorization(currentMember) {
    currentMember ? setAuth({
      userId: currentMember.id,
      status: "AUTHORIZED" /* AUTHORIZED */
    }) : setAuth({
      userId: null,
      status: "UNAUTHORIZED" /* UNAUTHORIZED */
    });
    return;
  }
  function getMember() {
    return __async(this, null, function* () {
      const { data: currentMember } = yield memberstack.getCurrentMember();
      getMemberAuthorization(currentMember);
    });
  }
  React3.useEffect(() => {
    let isMounted = true;
    let listener;
    if (isMounted) {
      listener = memberstack == null ? void 0 : memberstack.onAuthChange(getMemberAuthorization);
      getMember();
    }
    return () => {
      isMounted = false;
      listener == null ? void 0 : listener.unsubscribe();
    };
  }, []);
  return __spreadProps(__spreadValues({}, auth), {
    isLoggedIn: (auth == null ? void 0 : auth.status) === "AUTHORIZED" /* AUTHORIZED */,
    getToken: () => memberstack.getMemberCookie(),
    signOut: () => __async(void 0, null, function* () {
      return yield memberstack.logout();
    })
  });
};

// src/hooks/useMember.tsx
import React4 from "react";
var useMember = () => {
  const memberstack = useMemberstack();
  const [member, setMember] = React4.useState(null);
  const [memberJSON, setMemberJSON] = React4.useState(null);
  function getMember() {
    return __async(this, null, function* () {
      try {
        const { data: currentMember } = yield memberstack.getCurrentMember();
        currentMember ? setMember(currentMember) : setMember(null);
        const { data: jsonData } = yield memberstack.getMemberJSON();
        jsonData ? setMemberJSON(jsonData == null ? void 0 : jsonData.json) : setMemberJSON(null);
      } catch (err) {
        console.log(err);
      }
    });
  }
  React4.useEffect(() => {
    getMember();
  }, []);
  return {
    member,
    memberJSON,
    updateEmail: (email) => __async(void 0, null, function* () {
      return yield memberstack.updateMemberAuth({ email });
    }),
    updatePassword: (oldPassword, newPassword) => __async(void 0, null, function* () {
      return yield memberstack.updateMemberAuth({ oldPassword, newPassword });
    }),
    updateCustomFields: (customFields) => __async(void 0, null, function* () {
      return yield memberstack.updateMember({ customFields });
    }),
    updateMemberJSON: (json) => __async(void 0, null, function* () {
      return yield memberstack.updateMemberJSON({ json });
    })
  };
};

// src/hooks/useMemberstackModal/index.tsx
import React5 from "react";

// src/utils/index.ts
var filterNullableValues = (obj, { includeFalseVals = false } = {}) => Object.entries(obj).filter(([, y]) => includeFalseVals ? y !== null && y !== void 0 : y).reduce((acc, cur) => __spreadProps(__spreadValues({}, acc), { [cur[0]]: cur[1] }), {});

// src/hooks/useMemberstackModal/index.tsx
var useMemberstackModal = () => {
  const memberstack = useMemberstack();
  const config = React5.useRef({});
  const [show, setShow] = React5.useState(false);
  function openModal(openModalProps) {
    config.current = filterNullableValues(__spreadProps(__spreadValues({}, config.current), {
      type: openModalProps == null ? void 0 : openModalProps.type,
      planId: openModalProps.planId
    }));
    setShow(true);
    return memberstack.openModal(
      config.current.type,
      filterNullableValues({
        planId: config.current.planId
      })
    );
  }
  function hideModal() {
    setShow(false);
    return memberstack.hideModal();
  }
  return {
    openModal,
    hideModal
  };
};

// src/hooks/payments/useCheckout.tsx
var useCheckout = () => {
  const memberstack = useMemberstack();
  const checkout = (params) => __async(void 0, null, function* () {
    return yield memberstack.purchasePlansWithCheckout(
      //@ts-ignore
      filterNullableValues(
        {
          priceId: params.priceId,
          cancelUrl: params.cancelUrl,
          successUrl: params.successUrl,
          autoRedirect: params.autoRedirect
        },
        { includeFalseVals: true }
      )
    );
  });
  return checkout;
};

// src/hooks/payments/useCustomerPortal.tsx
var useCustomerPortal = (params) => {
  const memberstack = useMemberstack();
  const launchPortal = () => __async(void 0, null, function* () {
    return yield memberstack.launchStripeCustomerPortal(
      //@ts-ignore
      filterNullableValues(
        {
          priceIds: params.priceIds,
          returnUrl: params.returnUrl,
          autoRedirect: params.autoRedirect
        },
        { includeFalseVals: true }
      )
    );
  });
  return launchPortal;
};

// src/components/modal.tsx
import React6 from "react";
var ModalType = /* @__PURE__ */ ((ModalType2) => {
  ModalType2["LOGIN"] = "LOGIN";
  ModalType2["SIGNUP"] = "SIGNUP";
  ModalType2["FORGOT_PASSWORD"] = "FORGOT_PASSWORD";
  ModalType2["RESET_PASSWORD"] = "RESET_PASSWORD";
  ModalType2["PROFILE"] = "PROFILE";
  return ModalType2;
})(ModalType || {});
var modalFactory = (type) => {
  return function Modal(props) {
    const { openModal, hideModal } = useMemberstackModal();
    const onSuccess = props == null ? void 0 : props.onSuccess;
    const onError = props == null ? void 0 : props.onError;
    const onClose = props == null ? void 0 : props.onClose;
    const handleModal = () => __async(this, null, function* () {
      try {
        const { data } = yield openModal(__spreadProps(__spreadValues({}, props), { type }));
        onSuccess == null ? void 0 : onSuccess({ data, type });
        hideModal();
        onClose == null ? void 0 : onClose();
      } catch (error) {
        onError == null ? void 0 : onError(error);
        hideModal();
        onClose == null ? void 0 : onClose();
      }
    });
    React6.useEffect(() => {
      handleModal();
    }, []);
    return /* @__PURE__ */ React6.createElement(React6.Fragment, null);
  };
};
var SignInModal = modalFactory("LOGIN" /* LOGIN */);
var SignUpModal = modalFactory("SIGNUP" /* SIGNUP */);
var ForgotPasswordModal = modalFactory("FORGOT_PASSWORD" /* FORGOT_PASSWORD */);
var ResetPasswordModal = modalFactory("RESET_PASSWORD" /* RESET_PASSWORD */);
var ProfileModal = modalFactory("PROFILE" /* PROFILE */);

// src/components/control/auth.tsx
import React7 from "react";
var logicWrapper = (fn) => {
  return function LogicWrapper(props) {
    const auth = useAuth();
    const condition = React7.useMemo(() => fn(auth), [auth]);
    if (auth.status === "LOADING" /* LOADING */) {
      return props.fallback || null;
    }
    return condition ? props.children : null;
  };
};
var LoggedIn = logicWrapper((auth) => auth.isLoggedIn);
var LoggedOut = logicWrapper((auth) => !auth.isLoggedIn);

// src/index.tsx
console.log("Memberstack React has initialized");
export {
  ForgotPasswordModal,
  LoggedIn,
  LoggedOut,
  MemberstackProtected,
  MemberstackProvider,
  ModalType,
  ProfileModal,
  ResetPasswordModal,
  SignInModal,
  SignUpModal,
  useAuth,
  useCheckout,
  useCustomerPortal,
  useMember,
  useMemberstack,
  useMemberstackModal
};
